/**
 * AboutSidebar — full-screen "manifesto" takeover that opens on logo click.
 *
 * Sincere statement of what Prompt-Brush is, backed by live numbers and a
 * carousel of featured drawings (with their prompt + author) from the gallery
 * that auto-scrolls and reshuffles at random.
 *
 * Typography: the large manifesto is set in a serif. Delcan Mono is ALWAYS at
 * one size (DELCAN_SIZE) — the wordmark + stats. Small captions use FK Grotesk.
 */

import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import { useApp } from '@/contexts/AppContext';
import { fetchStats } from '@/services/supabase-storage';
import { StatsCarousel, type StatSlide } from './StatsCarousel';
import { APP_STORE_URL } from '@/config/appStore';
import {
  computePromptThemes,
  computePromptLengths,
  computeTopArtists,
  computeDrawnVsWaiting,
  computeTimeToDraw,
  computeTopWords,
  computeArtistDistribution,
  computePromptsByMonth,
  computeBusiestDay,
  computeFunFacts,
  computeReturning,
  computeMarks,
  computeQueueAge,
} from '@/utils/promptThemes';

interface AboutSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const MONO = 'FK Grotesk Mono, monospace';
const DISPLAY = 'Delcan Mono, monospace';
const SERIF = 'Martina Plantijn, Georgia, serif';
const DELCAN_SIZE = 'text-2xl';

// Real approved artworks (url + prompt + author), used ONLY for local dev preview
// (production uses live gallery data).
const DEV_SAMPLE = import.meta.env.DEV
  ? [
      { url: 'https://pub-18810369459947499c64f850f273396b.r2.dev/artworks/artwork_1780658787740_g1eseugx4/175f45dc-e540-4c56-9bd6-d972a91779cc.webp', prompt: 'unas salchichas felices', artist: 'Fryfaced' },
      { url: 'https://pub-18810369459947499c64f850f273396b.r2.dev/artworks/artwork_1780602256637_9favsaywj/75befcd4-c3d3-4446-8297-324f4a8d3d75.webp', prompt: 'Dove descending through flames', artist: 'ubin' },
      { url: 'https://pub-18810369459947499c64f850f273396b.r2.dev/artworks/artwork_1780571843246_7jbe31wfo/4b5eefed-1d5b-42a5-befd-6d2f588fdb3a.webp', prompt: 'a swan dreaming', artist: 'Alex Malwerkstatt' },
      { url: 'https://pub-18810369459947499c64f850f273396b.r2.dev/artworks/artwork_1780561831428_58wkqtmax/453cd3c2-e815-4c7a-bdb3-2fba31cf0493.webp', prompt: 'A bottle of wine', artist: 'Eddie Rochford whelan' },
      { url: 'https://pub-18810369459947499c64f850f273396b.r2.dev/artworks/artwork_1780553883237_t9g48io45/e9e5e80e-c6ac-4be5-9ef2-d2b2406484af.webp', prompt: 'A cat enjoying a bowl of ramen', artist: 'Eve Miller' },
      { url: 'https://pub-18810369459947499c64f850f273396b.r2.dev/artworks/artwork_1780540042738_94p1uadwk/40aa0e93-e1e0-4fba-8ffb-260e8ba4072a.webp', prompt: 'A broken heart', artist: 'Perritobebe' },
      { url: 'https://pub-18810369459947499c64f850f273396b.r2.dev/artworks/artwork_1780529507609_jmypreqro/6c27282d-02f8-46f3-9d4b-3e4f6338dbb5.webp', prompt: '3 beautiful floating chairs', artist: 'cheezus' },
      { url: 'https://pub-18810369459947499c64f850f273396b.r2.dev/artworks/artwork_1780518585512_we8roo7bh/85d81bd6-0ea8-48c5-b7f7-eb35793c650b.webp', prompt: 'A cactus with eyes', artist: 'Bluecalx' },
      { url: 'https://pub-18810369459947499c64f850f273396b.r2.dev/artworks/artwork_1780508632901_34xglj2a0/94d76158-67d3-4955-add0-88de81512063.webp', prompt: 'a cup of sunshine', artist: 'Alex Malwerkstatt' },
      { url: 'https://pub-18810369459947499c64f850f273396b.r2.dev/artworks/artwork_1780507775744_9v94tdk6c/a9954263-b293-4bf4-b7fd-0562c13ef1b2.webp', prompt: 'a sad girl crying in her office chair with iced coffee in her hand', artist: "we'rejustgirls" },
    ]
  : [];

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export function AboutSidebar({ isOpen, onClose }: AboutSidebarProps) {
  const navigate = useNavigate();
  const { prompts, artworks, refreshPrompts, refreshArtworks } = useApp();
  const [seed, setSeed] = useState(0);
  const [people, setPeople] = useState<number | null>(null);
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start', dragFree: true });

  // Live numbers, derived from the gallery data already in memory.
  const promptCount = prompts.length;
  const drawingCount = artworks.length;
  const artistCount = useMemo(
    () => new Set(artworks.map((a) => (a.artistName || '').trim()).filter(Boolean)).size,
    [artworks]
  );

  // "People" (unique submitters) is computed server-side and fetched from /api/stats,
  // so raw emails never reach the browser.

  // Featured drawings: a random subset (url + prompt + author) that reshuffles for variety.
  const featured = useMemo(() => {
    void seed; // re-shuffle whenever seed changes
    const live = artworks
      .map((a: any) => ({
        url: a.imageUrl || a.image || a.imageData,
        prompt: (a.promptText || a.prompt || '').trim(),
        artist: (a.artistName || '').trim(),
      }))
      .filter((x) => x.url);
    const pool = live.length > 0 ? live : DEV_SAMPLE;
    return shuffle(pool).slice(0, 12);
  }, [artworks, seed]);

  // Live, varied analysis of the project's data — each rendered as a different chart.
  const promptThemes = useMemo(() => computePromptThemes(prompts as any[]), [prompts]);
  const promptLengths = useMemo(() => computePromptLengths(prompts as any[]), [prompts]);
  const topArtists = useMemo(() => computeTopArtists(artworks as any[]), [artworks]);
  const drawnVsWaiting = useMemo(() => computeDrawnVsWaiting(prompts as any[], artworks as any[]), [prompts, artworks]);
  const topWords = useMemo(() => computeTopWords(prompts as any[]), [prompts]);
  const timeToDraw = useMemo(() => computeTimeToDraw(prompts as any[], artworks as any[]), [prompts, artworks]);
  const artistDist = useMemo(() => computeArtistDistribution(artworks as any[]), [artworks]);
  const byMonth = useMemo(() => computePromptsByMonth(prompts as any[]), [prompts]);
  const busiestDay = useMemo(() => computeBusiestDay(artworks as any[]), [artworks]);
  const returning = useMemo(() => computeReturning(artworks as any[]), [artworks]);
  const marks = useMemo(() => computeMarks(artworks as any[]), [artworks]);
  const queue = useMemo(() => computeQueueAge(prompts as any[], artworks as any[]), [prompts, artworks]);
  const funFacts = useMemo(() => computeFunFacts(prompts as any[], artworks as any[]), [prompts, artworks]);

  // Assemble the stat slides for the carousel — drop empty charts; facts close it out.
  const statSlides = useMemo<StatSlide[]>(() => {
    const charts: StatSlide[] = [
      { type: 'donut', title: 'What people ask us to draw', items: promptThemes },
      { type: 'bars', title: 'The words you reach for most', items: topWords },
      { type: 'columns', title: 'How many words is a prompt?', items: promptLengths },
      { type: 'donut', title: 'Drawn, or still waiting?', items: drawnVsWaiting },
      { type: 'columns', title: 'How long until a prompt is drawn?', items: timeToDraw.buckets },
      { type: 'bars', title: 'Who has drawn the most', items: topArtists },
      { type: 'columns', title: 'How many drawings each artist makes', items: artistDist },
      { type: 'columns', title: 'How many weeks people come back', items: returning.buckets },
      { type: 'columns', title: 'Prompts asked, by month', items: byMonth },
      { type: 'columns', title: 'When the community draws', items: busiestDay },
    ];
    const withData = charts.filter((s) => 'items' in s && s.items.length > 0);
    const factsSlide: StatSlide = {
      type: 'facts',
      title: 'A few more things',
      facts: [
        { value: funFacts.drawingsThisWeek, label: 'drawings this week' },
        { value: funFacts.promptsToday, label: 'prompts today' },
        { value: funFacts.avgWords, label: 'words · average prompt' },
        {
          value:
            timeToDraw.medianDays == null ? '—' : timeToDraw.medianDays === 0 ? '<1' : timeToDraw.medianDays,
          label: 'days to be drawn · median',
        },
        { value: topWords[0]?.label ?? '—', label: 'most-used word' },
        { value: returning.regulars, label: 'regulars · drew in 4+ different weeks' },
        { value: marks.total, label: 'marks left on drawings' },
        { value: `${Math.round(marks.share * 100)}%`, label: 'drawings with at least one mark' },
        { value: queue.medianDays == null ? '—' : queue.medianDays, label: 'days the waiting prompts have waited · median' },
      ],
      longest: funFacts.longest,
      shortest: funFacts.shortest,
    };
    return [...withData, factsSlide];
  }, [promptThemes, topWords, promptLengths, drawnVsWaiting, timeToDraw, topArtists, artistDist, byMonth, busiestDay, funFacts, returning, marks, queue]);

  // Close on Escape.
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape' && isOpen) onClose();
    }
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Auto-scroll the carousel and reshuffle the featured set while open.
  useEffect(() => {
    if (!isOpen) return;
    const advance = setInterval(() => emblaApi?.scrollNext(), 2800);
    const reshuffle = setInterval(() => setSeed((s) => s + 1), 15000);
    return () => {
      clearInterval(advance);
      clearInterval(reshuffle);
    };
  }, [isOpen, emblaApi]);

  // Keep the stats + charts live: refetch when the panel opens, then gently poll
  // (every 30s) while it stays open, so the numbers tick up without a page reload.
  useEffect(() => {
    if (!isOpen) return;
    const tick = () => {
      refreshPrompts();
      refreshArtworks();
      fetchStats().then((s) => setPeople(s.people)).catch(() => {});
    };
    tick();
    const id = setInterval(tick, 30000);
    return () => clearInterval(id);
  }, [isOpen, refreshPrompts, refreshArtworks]);

  const go = (path: string) => {
    onClose();
    navigate(path);
  };

  return (
    <div
      className={`fixed inset-0 z-[10000] bg-background transition-all duration-500 ease-out ${
        isOpen ? 'opacity-100 scale-100 pointer-events-auto' : 'opacity-0 scale-[1.02] pointer-events-none'
      }`}
      aria-hidden={!isOpen}
    >
      {/* Top bar: wordmark + close */}
      <div className="flex items-center justify-between px-6 md:px-12 pt-6">
        <span className={`text-foreground ${DELCAN_SIZE}`} style={{ fontFamily: DISPLAY }}>
          Prompt-Brush
        </span>
        <button
          onClick={onClose}
          className="p-2 rounded-full hover:bg-accent transition-colors text-foreground"
          aria-label="Close"
        >
          <X className="w-6 h-6" />
        </button>
      </div>

      {/* Body */}
      <div className="h-[calc(100%-72px)] overflow-y-auto">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-16">
          <p
            className="text-muted-foreground uppercase tracking-widest mb-8"
            style={{ fontSize: '10pt', fontFamily: MONO }}
          >
            The first non-AI generative art model
          </p>

          {/* Manifesto — serif */}
          <h1
            className="text-foreground text-3xl md:text-5xl leading-[1.22] max-w-3xl"
            style={{ fontFamily: SERIF, letterSpacing: '0.01em' }}
          >
            You{' '}
            <span className="underline decoration-1 underline-offset-[6px]">pick a prompt</span>{' '}
            someone wrote, you{' '}
            <span className="underline decoration-1 underline-offset-[6px]">draw it</span>, the
            person who wrote it receives it. I feel like I got so much from this exercise of
            drawing things for other people when I started{' '}
            <span
              onClick={() => go('/collection')}
              className="italic cursor-pointer underline-offset-[6px] hover:underline"
            >
              Prompt-Brush 1.0
            </span>{' '}
            that I’m curious and excited to see if other people might get something out of it
            too. A drawing made by a person for another person feels like a small good thing
            right now. Draw something or send in a prompt!
          </h1>

          {/* Live stats */}
          <div className="flex flex-wrap gap-12 md:gap-20 mt-14">
            {([
              { value: promptCount, label: 'Prompts' },
              { value: people, label: 'People' },
              { value: drawingCount, label: 'Drawings' },
              { value: artistCount, label: 'Artists' },
            ] as { value: number | null; label: string }[])
              .filter((s): s is { value: number; label: string } => s.value != null)
              .map((s) => (
              <div key={s.label} className="flex flex-col">
                <span
                  className={`text-foreground ${DELCAN_SIZE} tabular-nums`}
                  style={{ fontFamily: DISPLAY }}
                  data-stat={s.label.toLowerCase()}
                >
                  {s.value.toLocaleString()}
                </span>
                <span
                  className="text-muted-foreground uppercase tracking-widest mt-1"
                  style={{ fontSize: '9pt', fontFamily: MONO }}
                >
                  {s.label}
                </span>
              </div>
            ))}
          </div>

          {/* Live, halftone B&W analysis — a series of different charts */}
          <StatsCarousel slides={statSlides} />

          <p
            className="text-muted-foreground mt-12"
            style={{ fontFamily: MONO, fontSize: '9pt' }}
          >
            Live, from {promptCount.toLocaleString()} prompts and {drawingCount.toLocaleString()} drawings.
          </p>

          {/* Calls to action */}
          <div className="flex flex-wrap gap-3 mt-12">
            {APP_STORE_URL && (
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full px-6 py-3 bg-foreground text-background hover:opacity-80 transition-opacity"
                style={{ fontFamily: MONO, fontSize: '11pt' }}
              >
                Get the iPhone app
              </a>
            )}
            <button
              onClick={() => go('/prompts')}
              className="border border-border rounded-full px-6 py-3 text-foreground hover:bg-foreground hover:text-background transition-colors"
              style={{ fontFamily: MONO, fontSize: '11pt' }}
            >
              Browse the prompts
            </button>
            <button
              onClick={() => go('/collection')}
              className="border border-border rounded-full px-6 py-3 text-foreground hover:bg-foreground hover:text-background transition-colors"
              style={{ fontFamily: MONO, fontSize: '11pt' }}
            >
              The 1.0 drawings
            </button>
            <button
              onClick={() => go('/book')}
              className="border border-border rounded-full px-6 py-3 text-foreground hover:bg-foreground hover:text-background transition-colors"
              style={{ fontFamily: MONO, fontSize: '11pt' }}
            >
              Get the book
            </button>
            <button
              onClick={() => go('/shop')}
              className="border border-border rounded-full px-6 py-3 text-foreground hover:bg-foreground hover:text-background transition-colors"
              style={{ fontFamily: MONO, fontSize: '11pt' }}
            >
              The shop
            </button>
          </div>

          {/* Contact */}
          <div className="mt-14">
            <p
              className="text-muted-foreground uppercase tracking-widest mb-2.5"
              style={{ fontSize: '9pt', fontFamily: MONO }}
            >
              Get in touch
            </p>
            <a
              href="mailto:pablo@delcan.co"
              className="text-foreground underline underline-offset-[5px] decoration-1 hover:opacity-70 transition-opacity"
              style={{ fontFamily: DISPLAY, fontSize: '1.05rem' }}
            >
              pablo@delcan.co
            </a>
            <p
              className="text-muted-foreground mt-3 max-w-md"
              style={{ fontFamily: SERIF, fontSize: '13px', lineHeight: 1.5 }}
            >
              Questions, ideas, or just a hello — we'd love to hear from you.
            </p>
          </div>

          {/* Featured drawings carousel — with prompt + author */}
          {featured.length > 0 && (
            <div className="mt-16">
              <p
                className="text-muted-foreground uppercase tracking-widest mb-4"
                style={{ fontSize: '9pt', fontFamily: MONO }}
              >
                Drawings from the community
              </p>
              <div className="overflow-hidden -mx-3" ref={emblaRef}>
                <div className="flex">
                  {featured.map((item, i) => (
                    <div key={`${item.url}-${i}`} className="flex-[0_0_auto] w-60 md:w-80 px-3">
                      <img
                        src={item.url}
                        alt={item.prompt || 'Community drawing'}
                        className="w-full rounded-md border border-border bg-card object-cover"
                        style={{ aspectRatio: '5/7' }}
                        loading="lazy"
                        draggable={false}
                      />
                      <div className="mt-3">
                        {item.prompt && (
                          <div
                            className="text-foreground leading-snug line-clamp-2"
                            style={{ fontFamily: MONO, fontSize: '11pt' }}
                            title={item.prompt}
                          >
                            {item.prompt}
                          </div>
                        )}
                        {item.artist && (
                          <div
                            className="text-muted-foreground uppercase tracking-widest mt-1.5 truncate"
                            style={{ fontFamily: MONO, fontSize: '8.5pt' }}
                          >
                            {item.artist}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
